import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Image,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
} from '@chakra-ui/react';
import { LuCalendar } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { FILTER_OPERATORS, resolveTimePreset } from '../../../utils/filterConstants';
import { translateOptionDisplayName, translateOperatorLabel } from './filterDisplayHelpers';

// ============================================================
// Exported Standalone Filter Panels (Box-based, reusable)
// Used in both FilterMenu submenus and chip edit Popovers
// ============================================================

/**
 * CheckboxFilterPanel — checkbox list only (operator is controlled from the chip)
 * @param {Array} existingValues - Pre-resolved values for this specific chip (bypasses selectedFilters lookup)
 */
export const CheckboxFilterPanel = ({ filter, selectedFilters, onAddFilter, initialOperator, existingValues, searchQuery, hideSearch, autoFocusSearch = true }) => {
  const { t } = useTranslation('filters');
  const operator = initialOperator || FILTER_OPERATORS.IN;

  // Use existingValues if provided (for scoped chip editing), otherwise lookup from selectedFilters
  const selectedValues = existingValues !== undefined
    ? existingValues
    : (selectedFilters.find(
        f => f.entity === filter.entity && f.column === filter.column && f.operator === operator
      )?.values || []);

  const [localSearch, setLocalSearch] = useState('');

  const filterTerm = localSearch || searchQuery || '';
  const visibleOptions = filterTerm
    ? (filter.options || []).filter(opt =>
        opt.displayName?.toLowerCase().includes(filterTerm.toLowerCase())
      )
    : (filter.options || []);

  const handleToggle = (value) => {
    const updated = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onAddFilter({ entity: filter.entity, column: filter.column, operator, values: updated });
  };

  return (
    <Box
      maxH="300px"
      overflowY="auto"
      sx={{
        '&::-webkit-scrollbar': { width: '0px' },
        '&:hover::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { bg: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bg: 'redesign.border.strong', borderRadius: 'full' },
        scrollbarWidth: 'none',
        '&:hover': { scrollbarWidth: 'thin' },
      }}
    >
      {!hideSearch && (
        <>
          <Flex px="14px" h="38px" align="center" position="sticky" top={0} bg="redesign.bg.popup" zIndex={1}>
            <Input
              variant="unstyled"
              size="xs"
              placeholder={t('panels.searchPlaceholder')}
              fontSize="13px"
              color="redesign.text"
              _placeholder={{ color: "redesign.text.muted" }}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              autoFocus={autoFocusSearch && !searchQuery}
            />
          </Flex>
          <Divider />
        </>
      )}
      {visibleOptions.length === 0 ? (
        <Box px="14px" py={2}>
          <Text fontSize="13px" color="redesign.text.muted">{t('panels.noOptions')}</Text>
        </Box>
      ) : (
        <Box py="6px">
        {visibleOptions.map(option => (
          // Linear's submenu row: 28px, 3px apart, inset 6px, rounded.
          <Flex
            key={option.value}
            mx="6px"
            mb="3px"
            px="8px"
            h="28px"
            cursor="pointer"
            _hover={{ bg: "redesign.bg.hover" }}
            borderRadius="6px"
            align="center"
            onClick={() => handleToggle(option.value)}
          >
            <Checkbox
              isChecked={selectedValues.includes(option.value)}
              pointerEvents="none"
              spacing="10px"
              size="sm"
            >
              <Flex align="center" gap={2} opacity={option.isActive === false ? 0.6 : 1}>
                {/* An institution is known by its logo, not by an emoji, so it
                    arrives as an image and falls back to whatever `icon` holds
                    if the asset is missing (MI-139). */}
                {option.iconUrl && (
                  <Image
                    src={option.iconUrl}
                    alt=""
                    boxSize="16px"
                    objectFit="contain"
                    borderRadius="sm"
                    flexShrink={0}
                    fallback={<Text fontSize="sm" lineHeight={1} flexShrink={0}>{option.icon || '🏦'}</Text>}
                  />
                )}
                {!option.iconUrl && option.icon && (
                  <Text fontSize="sm" lineHeight={1} flexShrink={0}>{option.icon}</Text>
                )}
                {!option.iconUrl && !option.icon && option.color && (
                  <Box w="8px" h="8px" borderRadius="full" bg={option.color} flexShrink={0} />
                )}
                <Text fontSize="13px" fontWeight="400" color={option.isActive === false ? "redesign.text.muted" : "redesign.text"}>
                  {translateOptionDisplayName(filter.entity, filter.column, option.value, option.displayName)}
                </Text>
              </Flex>
            </Checkbox>
          </Flex>
        ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * NumericFilterPanel — number input + Apply (operator controlled from chip)
 */
export const NumericFilterPanel = ({ filter, onAddFilter, onClose, initialOperator, initialValue, autoFocusSearch = true }) => {
  const { t } = useTranslation('filters');
  const operator = initialOperator || FILTER_OPERATORS.GTE;
  const [value, setValue] = useState(initialValue != null ? String(initialValue) : '');

  const handleApply = () => {
    if (value === '' || isNaN(Number(value))) return;
    onAddFilter({ entity: filter.entity, column: filter.column, operator, values: [Number(value)] });
    onClose?.();
  };

  return (
    <Box p={2}>
      <NumberInput size="xs" mb={2}>
        <NumberInputField
          placeholder={t('panels.valuePlaceholder')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
          autoFocus={autoFocusSearch}
        />
      </NumberInput>
      <Button size="xs" variant="primary" w="100%" onClick={handleApply}>{t('panels.apply')}</Button>
    </Box>
  );
};

/**
 * TextFilterPanel — operator dropdown + text input + Apply
 */
export const TextFilterPanel = ({ filter, onAddFilter, onClose, initialOperator, initialValue }) => {
  const { t } = useTranslation('filters');
  const operators = (filter.supportedOperators || ['ilike', 'like'])
    .filter(op => [FILTER_OPERATORS.ILIKE, FILTER_OPERATORS.LIKE].includes(op));
  const [operator, setOperator] = useState(initialOperator || operators[0] || FILTER_OPERATORS.ILIKE);
  const [value, setValue] = useState(initialValue || '');

  const handleApply = () => {
    if (!value.trim()) return;
    onAddFilter({ entity: filter.entity, column: filter.column, operator, values: [value.trim()] });
    onClose?.();
  };

  return (
    <Box p={2}>
      <Select size="xs" mb={2} value={operator} onChange={(e) => setOperator(e.target.value)}>
        {operators.map(op => (
          <option key={op} value={op}>{translateOperatorLabel(op)}</option>
        ))}
      </Select>
      <Input
        size="xs"
        placeholder={t('panels.textPlaceholder')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
        mb={2}
      />
      <Button size="xs" variant="primary" w="100%" onClick={handleApply}>{t('panels.apply')}</Button>
    </Box>
  );
};

/**
 * DateFilterPanel — time presets + "Custom date..." button
 * The AdvancedDatePicker modal is rendered by the parent (FilterMenu / FilterBar)
 * so it survives menu/popover closure.
 */
const DEFAULT_DATE_PRESETS = [
  // Hours only mean something on a timestamp such as created_at (MI-315);
  // a date column holds no hour, so the panel leaves them out there.
  { presetKey: '1h', offsetValue: 1, offsetUnit: 'hours', operator: 'gte' },
  { presetKey: '6h', offsetValue: 6, offsetUnit: 'hours', operator: 'gte' },
  { presetKey: '12h', offsetValue: 12, offsetUnit: 'hours', operator: 'gte' },
  { presetKey: '1d', offsetValue: 1, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '3d', offsetValue: 3, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '1w', offsetValue: 7, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '1mo', offsetValue: 30, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '3mo', offsetValue: 90, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '6mo', offsetValue: 180, offsetUnit: 'days', operator: 'gte' },
  { presetKey: '1y', offsetValue: 1, offsetUnit: 'years', operator: 'gte' },
];

// A row of the date list, the same 28px inset row as the menu's.
const PresetRow = ({ icon, onClick, children }) => (
  <Flex
    as="button"
    type="button"
    align="center"
    gap="10px"
    w="calc(100% - 12px)"
    mx="6px"
    mb="3px"
    px="8px"
    h="28px"
    borderRadius="6px"
    fontSize="13px"
    color="redesign.text"
    textAlign="left"
    _hover={{ bg: 'redesign.bg.hover' }}
    onClick={onClick}
  >
    {icon && <Box as={icon} boxSize="14px" color="redesign.text.muted" flexShrink={0} />}
    <Text as="span" isTruncated>{children}</Text>
  </Flex>
);

export const DateFilterPanel = ({ filter, onAddFilter, onClose, timePresets = [], initialOperator, initialValue, onOpenAdvancedPicker }) => {
  const { t } = useTranslation('filters');
  const apiPresets = timePresets.filter(
    p => p.targetColumn === filter.column && p.targetEntity === filter.entity && (p.label || p.displayName)
  );
  // Use API presets if available, otherwise fall back to defaults
  const relevantPresets = apiPresets.length > 0
    ? apiPresets
    : DEFAULT_DATE_PRESETS
      .filter(p => p.offsetUnit !== 'hours' || filter.columnType === 'datetime')
      .map(p => ({ ...p, targetColumn: filter.column, targetEntity: filter.entity }));

  const getPresetLabel = (preset) => {
    if (preset.presetKey) return t(`datePresets.${preset.presetKey}`);
    return preset.label || preset.displayName;
  };

  const handlePreset = (preset) => {
    const resolved = resolveTimePreset(preset);
    if (!resolved) return;
    onAddFilter({
      entity: filter.entity,
      column: filter.column,
      operator: preset.operator || FILTER_OPERATORS.GTE,
      values: [resolved],
      presetLabel: getPresetLabel(preset)
    });
    onClose?.();
  };

  return (
    <Box py="6px">
      {relevantPresets.length > 0 && (
        <>
          {relevantPresets.map((preset, idx) => (
            <PresetRow key={idx} onClick={() => handlePreset(preset)}>
              {getPresetLabel(preset)}
            </PresetRow>
          ))}
          <Divider my="6px" />
        </>
      )}
      <PresetRow
        icon={LuCalendar}
        onClick={() => {
          onOpenAdvancedPicker?.({
            filter,
            initialOperator: initialOperator || FILTER_OPERATORS.GTE,
            initialValue,
          });
          onClose?.();
        }}
      >
        {t('panels.customDate')}
      </PresetRow>
    </Box>
  );
};

/**
 * Render the appropriate filter panel based on columnType
 */
export const renderFilterPanel = (filterDef, panelProps) => {
  const { columnType } = filterDef;
  const hasOptions = filterDef.options && filterDef.options.length > 0;

  if (columnType === 'date' || columnType === 'datetime') {
    return <DateFilterPanel filter={filterDef} {...panelProps} />;
  }
  if (columnType === 'numeric') {
    return <NumericFilterPanel filter={filterDef} {...panelProps} />;
  }
  if ((columnType === 'string' || columnType === 'id') && hasOptions) {
    return <CheckboxFilterPanel filter={filterDef} {...panelProps} />;
  }
  if (columnType === 'string' && !hasOptions) {
    return <TextFilterPanel filter={filterDef} {...panelProps} />;
  }
  if (hasOptions) {
    return <CheckboxFilterPanel filter={filterDef} {...panelProps} />;
  }
  return <TextFilterPanel filter={filterDef} {...panelProps} />;
};
